import { DqCard, DqCardContent, DqIcon } from "@decentriq/components";
import { faNewspaper } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormLabel,
  Grid,
  Modal,
  ModalDialog,
  Typography,
  useTheme,
} from "@mui/joy";
import { useSnackbar } from "notistack";
import {
  type ChangeEvent,
  Fragment,
  memo,
  type MouseEventHandler,
  useCallback,
} from "react";
import {
  Controller,
  type ControllerRenderProps,
  FormProvider,
  useForm,
} from "react-hook-form";
import {
  mediaDataRoomOrganizationRolePresentationMap,
  type MediaDataRoomParticipant,
} from "features/mediaDataRoom/models";
import {
  getMediaDataRoomAudienceShareDialogSchema,
  type MediaDataRoomAudienceShareDialogFormValues,
} from "./models";

export interface MediaDataRoomAudienceShareDialogProps {
  open: boolean;
  availableParticipants: MediaDataRoomParticipant[];
  isLoading: boolean;
  defaultSelected: string[];
  onCancel: () => void;
  onShare: (selectedParticipants: MediaDataRoomParticipant[]) => Promise<void>;
}

const MediaDataRoomAudienceShareDialog =
  memo<MediaDataRoomAudienceShareDialogProps>(
    ({
      open,
      onCancel,
      onShare,
      availableParticipants,
      isLoading,
      defaultSelected,
    }) => {
      const { breakpoints } = useTheme();
      const { enqueueSnackbar } = useSnackbar();
      const isUpdating = defaultSelected.length > 0;
      const form = useForm<MediaDataRoomAudienceShareDialogFormValues>({
        defaultValues: {
          selectedParticipantIds: defaultSelected,
        },
        mode: "onSubmit",
        resolver: zodResolver(
          getMediaDataRoomAudienceShareDialogSchema({ isUpdating })
        ),
      });
      const handleCancel = useCallback(() => {
        form.reset({ selectedParticipantIds: defaultSelected });
        onCancel?.();
      }, [form, onCancel, defaultSelected]);
      const handleShare = useCallback<
        MouseEventHandler<HTMLButtonElement>
      >(() => {
        void form.handleSubmit(
          ({ selectedParticipantIds }) => {
            const selectedParticipants = selectedParticipantIds
              .map((id) => availableParticipants.find((p) => p.id === id))
              .filter(Boolean) as MediaDataRoomParticipant[];
            void onShare(selectedParticipants);
          },
          (errors) => {
            if (errors.selectedParticipantIds) {
              enqueueSnackbar(errors.selectedParticipantIds.message);
            }
          }
        )();
      }, [form, onShare, availableParticipants, enqueueSnackbar]);
      const handleChange = useCallback(
        (
          event: ChangeEvent<HTMLInputElement>,
          field: ControllerRenderProps<
            MediaDataRoomAudienceShareDialogFormValues,
            "selectedParticipantIds"
          >,
          participant: MediaDataRoomParticipant
        ) => {
          const currentValue = Array.isArray(field.value) ? field.value : [];
          event.target.checked
            ? field.onChange([...currentValue, participant.id])
            : field.onChange(
                currentValue.filter((id) => id !== participant.id)
              );
        },
        []
      );
      return (
        <Modal open={open}>
          <ModalDialog
            maxWidth={breakpoints.values.md}
            minWidth={breakpoints.values.md}
          >
            <FormProvider {...form}>
              <DialogTitle>
                Choose the participant you want to share this audience with
              </DialogTitle>
              <DialogContent>
                <Fragment>
                  <Controller<
                    MediaDataRoomAudienceShareDialogFormValues,
                    "selectedParticipantIds"
                  >
                    control={form.control}
                    name="selectedParticipantIds"
                    render={({ field }) => (
                      <Fragment>
                        <FormLabel>Select one or more participants</FormLabel>
                        <Grid container={true} spacing={2} sx={{ mb: 2 }}>
                          {availableParticipants.map((participant) => (
                            <Grid key={participant.name} xs={4}>
                              <DqCard
                                selectable={{
                                  checked:
                                    Array.isArray(field.value) &&
                                    field.value.includes(participant.id),
                                  component: "checkbox",
                                  ...field,
                                  name: "selectedParticipantIds",
                                  onChange: (event) => {
                                    handleChange(event, field, participant);
                                  },
                                }}
                              >
                                <DqCardContent>
                                  <Typography level="body-sm">
                                    {
                                      mediaDataRoomOrganizationRolePresentationMap[
                                        participant.role
                                      ]
                                    }
                                  </Typography>
                                  <Typography
                                    fontWeight={500}
                                    level="body-md"
                                    sx={{
                                      mb: 2,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      width: "100%",
                                    }}
                                    textAlign="center"
                                  >
                                    {participant.name}
                                  </Typography>
                                  {participant.logo ? (
                                    <DqIcon
                                      icon={`data:image;base64,${participant.logo}`}
                                      iconProps={{
                                        height: "60px",
                                        width: "100%",
                                      }}
                                    />
                                  ) : (
                                    <FontAwesomeIcon
                                      icon={faNewspaper}
                                      size="3x"
                                    />
                                  )}
                                </DqCardContent>
                              </DqCard>
                            </Grid>
                          ))}
                        </Grid>
                      </Fragment>
                    )}
                  />
                  <Alert>
                    Once shared, this participant may be able to export this
                    audience (if they have the Export audiences permission).
                  </Alert>
                </Fragment>
              </DialogContent>
              <Divider />
              <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button
                  color="primary"
                  loading={isLoading}
                  onClick={handleShare}
                  variant="solid"
                >
                  {isUpdating ? "Update" : "Share"}
                </Button>
              </DialogActions>
            </FormProvider>
          </ModalDialog>
        </Modal>
      );
    }
  );

MediaDataRoomAudienceShareDialog.displayName =
  "MediaDataRoomAudienceShareDialog";
export default MediaDataRoomAudienceShareDialog;
